import { ALL_KINDS, ACTIONS_ONLY, CARD_KINDS, SELECTION_KINDS, NOTIFICATION_KINDS, type LayoutManifest } from "./manifest";

/**
 * Slot manifest for the single-menu layout. Drives the slot-mapping
 * editor's per-slot list, accepted block kinds, and labels.
 *
 * Keep this in sync with SingleMenuLayout.tsx's <Slot slotId="..."> calls
 * and SingleMenuLayout.defaults.ts's default content. New slots added to
 * the layout must be added here too or the editor can't surface them.
 */
export const SINGLE_MENU_MANIFEST: LayoutManifest = {
  id: "single-menu",
  slots: [
    {
      id: "tool-rail",
      label: "Tool rail (right edge)",
      description: "Vertical button stack — camera toggle, reset, undo/redo, delete, rotate, help. Floats over the canvas on both desktop and mobile.",
      accepts: ACTIONS_ONLY,
      target: "both",
    },
    {
      id: "left-panel",
      label: "Left side panel (desktop)",
      description: "Brand mark + finishes section + product catalog. Desktop only — on mobile its catalog/finishes feed the bottom-sheet tabs (see the Mobile slots).",
      accepts: ALL_KINDS,
      target: "desktop",
    },
    {
      id: "status-card",
      label: "Status card (desktop, bottom right)",
      description: "Floating composite — element count, price (when available), Back / Review actions. Desktop only — mobile uses the Cart tab in the bottom sheet instead.",
      accepts: CARD_KINDS,
      target: "desktop",
    },
    {
      id: "selection-popup",
      label: "On-selection popup",
      description: "Shown when a placed product is selected. Desktop: a card that slides up from the bottom. Mobile: takes over the bottom sheet. Default content: selected product name + the per-surface material picker.",
      accepts: SELECTION_KINDS,
      target: "both",
    },
    {
      id: "mobile:menu-tabs",
      label: "Mobile menu — tab captions",
      description: "Captions for the mobile bottom-sheet tab strip. Text blocks keyed 'materials', 'products', and 'cart' — reword or translate each.",
      accepts: ALL_KINDS,
      target: "mobile",
    },
    {
      id: "mobile:materials",
      label: "Materials tab",
      description: "Bottom-sheet 'Materials' tab (mobile only). Default content: the finishes/material-collections picker. Leave empty to inherit the side panel's finishes.",
      accepts: ALL_KINDS,
      target: "mobile",
    },
    {
      id: "mobile:products",
      label: "Products tab",
      description: "Bottom-sheet 'Products' tab (mobile only). Default content: the editorial-row product catalog (drag the row handle to place). Leave empty to inherit the side panel's catalog.",
      accepts: ALL_KINDS,
      target: "mobile",
    },
    {
      id: "series-picker-topbar",
      label: "Series picker — top bar",
      description: "Series picker chrome above the tile gallery. Brand mark + centre title.",
      accepts: ALL_KINDS,
      target: "both",
    },
    {
      id: "series-picker-intro",
      label: "Series picker — intro strip",
      description: "Intro strip above the tile gallery. Eyebrow + headline + body copy.",
      accepts: ALL_KINDS,
      target: "both",
    },
    {
      id: "series-picker-tiles",
      label: "Series picker — tiles",
      description: "The series tiles — these blocks render once per series (Series image, Series name, a text CTA). The picker owns the tile frame + click-to-pick.",
      accepts: ALL_KINDS,
      target: "both",
    },
    {
      id: "modal:cart",
      label: "Cart modal — body (desktop)",
      description: "Element list shown when the cart opens on desktop. Rendered inside a modal frame. On mobile the Cart modal — body (mobile) slot is used instead (falling back to this one when that's empty).",
      accepts: ALL_KINDS,
      target: "desktop",
    },
    {
      id: "modal:cart-mobile",
      label: "Cart modal — body (mobile)",
      description: "Element list shown when the cart opens at mobile widths. Author a narrower table here (e.g. drop the Article Number column and show the SKU under the product name). Leave empty to reuse the desktop cart body.",
      accepts: ALL_KINDS,
      target: "mobile",
    },
    {
      id: "modal:cart-footer",
      label: "Cart modal — footer actions",
      description: "Action buttons rendered next to CLOSE in the cart footer. Drop render-pdf-download / render-pdf-email action blocks here and set pdfLayoutId on each to wire up PDF buttons.",
      accepts: ACTIONS_ONLY,
      target: "both",
    },
    {
      id: "modal:help",
      label: "Help modal — body",
      description: "Help content shown when the Help action fires.",
      accepts: ALL_KINDS,
      target: "both",
    },
    {
      id: "modal:pdf-email-header",
      label: "Email modal — header",
      description: "The email modal's header — text blocks author the title + subtitle shown above the form.",
      accepts: ALL_KINDS,
      target: "both",
    },
    {
      id: "modal:email",
      label: "Email modal — body",
      description: "Email composition modal opened from the cart.",
      accepts: ALL_KINDS,
      target: "both",
    },
    {
      id: "modal:scene-looks",
      label: "Scene-looks modal — body",
      description: "Preset list shown by the 'Open scene-looks picker' action. Each action block placed here becomes one preset card; configure its label/icon and the (object name, DMat) pairs the button applies.",
      accepts: ALL_KINDS,
      target: "both",
    },
    {
      id: "notifications",
      label: "Notifications (top-of-screen toasts)",
      description: "Info toasts shown at the top of the screen (desktop + mobile). Each notification block has a trigger (e.g. product tapped without dragging, cart opened empty), a message, and a timeout (0 = stays until dismissed).",
      accepts: NOTIFICATION_KINDS,
      target: "both",
    },
    {
      id: "startup",
      label: "Startup actions (not rendered)",
      description: "Action blocks fired once after the configurator is ready, before the busy overlay clears. Typical use: apply-scene-materials to paint [WALL] and [FLOOR] with a default look so the scene boots with a chosen aesthetic instead of unassigned materials.",
      accepts: ACTIONS_ONLY,
      target: "both",
    },
  ],
};
